import { globals } from 'config/globals';
import { DOMHelper } from 'core/DOMHelper';
import { ConstrainAroundCircle } from 'app/chrome/mouse/ConstrainAroundCircle';
import { Inertia } from 'utils/motion/Inertia';
import { Point } from 'app/contracts/Geometry';
import fastdom from 'fastdom';
import DelayHelper from 'utils/timers';

import { MouseCursorViewDeps } from './mouse.types';
import { device } from 'utils/device';

/**
 * DOM side of the custom cursor: the drag-cursor / clone-dot / drag-prompt
 * elements and their class states (hidden, dark theme, arrow guides,
 * clickable, colour, pressed sides, vertical / both, transition), the
 * home-intro constrain flow, the hide-cursor and sync-cursor hover flows
 * (incl. the hover-lock inertia machinery) and the per-frame cursor
 * transform writes. Event wiring stays in MouseController (Events.on
 * registrations live there; handler bodies delegate in here).
 */
export class MouseCursorView {
	private _dom: DOMHelper;
	private _deps: MouseCursorViewDeps;
	private _container: Element;

	private _dragViewActive: boolean = false;
	private _$dragCursor: HTMLElement;
	private _$dragCursorCloneDot: HTMLElement;
	private _$dragPrompt: HTMLElement;

	private _pauseHoldingFlag: boolean;
	private _hidden: boolean = false;
	private _introActionActive: boolean = false;
	private _introConstrain: ConstrainAroundCircle = new ConstrainAroundCircle();

	private _hoverLocked: Boolean = false;
	private _hoverLockedProcessingActive: boolean = false;
	private _lockClass: string;
	private _lockPosition: string = 'absoulte';
	private _lockOrgCords: any = null;
	private _lockInertiaX: Inertia;
	private _lockInertiaY: Inertia;
	private _lockSupportInertiaX: Inertia;
	private _lockSupportInertiaY: Inertia;

	private $body: HTMLElement;

	private _clearLockhoverId = null;

	//NOTE: these six were UNDECLARED expando properties on the old
	//MouseController (assigned only inside the dead tail of _syncMouseOver, so
	//at runtime they stay undefined and every `if (this.$circle)`-style branch
	//is effectively dead). Declared here so the moved code compiles in its new
	//home; behavior is identical.
	public $circle: HTMLElement;
	public $circleClone: HTMLElement;
	public $text: HTMLElement;
	public $text2: HTMLElement;
	public $dot: HTMLElement;
	public cords: any;

	constructor(dom: DOMHelper, deps: MouseCursorViewDeps) {
		this._dom = dom;
		this._deps = deps;
	}

	//Init / element refs (called from MouseController — order preserved)
	//-------------------------------------------------------------------------------------

	public createInertias(): void {
		this._lockInertiaX = new Inertia(10, 0);
		this._lockInertiaY = new Inertia(10, 0);

		this._lockSupportInertiaX = new Inertia(10, 0);
		this._lockSupportInertiaY = new Inertia(10, 0);
	}

	public queryBody(): void {
		this.$body = document.querySelector('body');
	}

	public queryRefs(container: Element): void {
		this._container = container;

		this._$dragCursor = container.querySelector('.ui__cursordrag');
		this._$dragCursorCloneDot = container.querySelector('.ui__cursordrag__clonedot');
		this._$dragPrompt = container.querySelector('.drag-prompt');
	}

	public setOutOfBrowser(out: boolean): void {
		if (out) {
			this._dom.addClass(this._container, '-outofbrowser');
		} else {
			this._dom.removeClass(this._container, '-outofbrowser');
		}
	}

	//Per-frame / resize (called from MouseController — order preserved)
	//-------------------------------------------------------------------------------------

	public tick(): void {
		if (this._deps.isGuideFollowActive()) {
			fastdom.mutate(() => {
				this._$dragPrompt.style.setProperty('--mouse-delayed-x', `${this._deps.getDelayedX()}px`);
				this._$dragPrompt.style.setProperty('--mouse-delayed-y', `${this._deps.getDelayedY()}px`);
			});
		}

		this._lockInertiaX.tick();
		this._lockInertiaY.tick();
		this._lockSupportInertiaX.tick();
		this._lockSupportInertiaY.tick();

		let circleRect = null;

		if (this.$circle) {
			fastdom.measure(() => {
				circleRect = this.$circle.getBoundingClientRect();
			});
		}

		if (this._hoverLocked && circleRect) {
			//Make normal circle follow mouse

			fastdom.measure(() => {
				circleRect = {
					width: this._$dragCursor.clientWidth,
					height: this._$dragCursor.clientHeight
				};
			});

			if (this._lockPosition == 'relative') {
				this._lockInertiaX.target = this._deps.getMouseCords().x - this._lockOrgCords.x - 90;
				this._lockInertiaY.target = this._deps.getMouseCords().y - this._lockOrgCords.y - 90;
			} else {
				this._lockInertiaX.target =
					this._deps.getMouseCords().x - Math.round(circleRect.width / 2) - this._lockOrgCords.x;
				this._lockInertiaY.target =
					this._deps.getMouseCords().y - Math.round(circleRect.height / 2) - this._lockOrgCords.y;
			}

			//

			let add = this._hoverLocked ? 90 : 0;
			this._lockSupportInertiaX.target = this._lockInertiaX.target + add;
			this._lockSupportInertiaY.target = this._lockInertiaY.target * 0.3;
		}

		if (this._hoverLockedProcessingActive) {
			fastdom.mutate(() => {
				if (this.$circle) {
					this.$circle.style.transform = `translate3d(${this._lockInertiaX.value}px, ${this._lockInertiaY.value}px, 0)`;
				}

				if (this.$text) {
					this.$text.style.transform = `translate3d(${this._lockSupportInertiaX.value}px, ${this._lockSupportInertiaY.value}px, 0)`;
				}

				if (this.$dot) {
					this.$dot.style.transform = `translate3d(${this._lockInertiaX.value * 1 + 10}px, ${this._lockInertiaX.value * 1 + 10}px, 0)`;
				}

				if (this.$text2) {
					let ix = this._lockSupportInertiaX.value * 0;
					let iy = this._lockSupportInertiaY.value * 0;
					this.$text2.style.transform = `translate3d(${ix}px, ${iy}px, 0)`;
				}
			});
		}

		if ((this._dragViewActive || this._introActionActive) && !globals.lastCordsFromTouch) {
			fastdom.mutate(() => {
				if (this._introActionActive) {
					let introMousePoint: Point = this._introConstrain.limit(this._deps.getMouseCords());
					this._$dragCursor.style.transform = `translate3d(${introMousePoint.x}px, ${introMousePoint.y}px, 0)`;
					this._$dragCursorCloneDot.style.transform = `translate3d(${introMousePoint.x}px, ${introMousePoint.y}px, 0)`;
				} else {
					this._$dragCursor.style.transform = `translate3d(${this._deps.getMouseCords().x}px, ${this._deps.getMouseCords().y}px, 0)`;
				}
			});
		}
	}

	public resize(): void {
		this._introConstrain.setConstraints(
			{ x: device.innerWidth / 2, y: device.innerHeight * 0.5 },
			74
		);
	}

	//Pause / unpause (nav menu)
	//-------------------------------------------------------------------------------------

	/** When showing nav menu stop special state and save if active at time of pause */
	public pause(): void {
		this._pauseHoldingFlag = this._dragViewActive;
		this.setDragCursorActive(false, false);
	}

	/** When hiding nav menu show 'drag' state if it was showing at the time of pausing */
	public unpause(): void {
		if (this._pauseHoldingFlag) {
			this.setDragCursorActive(true);
		}
	}

	//Class states
	//-------------------------------------------------------------------------------------

	/** Background can be affected by the parallax in home / work sections */
	public setDarkTheme(isDarkTheme: boolean): void {
		if (isDarkTheme) {
			this._dom.addClass(this._$dragCursor, '-darktheme');
		} else {
			this._dom.removeClass(this._$dragCursor, '-darktheme');
		}
	}

	public addArrowGuide(type: string) {
		this.removeArrowGuides();
		this._dom.addClass(this._$dragCursor, '-drag-hint-' + type);
	}

	public removeArrowGuides() {
		this._dom.removeClass(this._$dragCursor, '-drag-hint-right');
		this._dom.removeClass(this._$dragCursor, '-drag-hint-left');
		this._dom.removeClass(this._$dragCursor, '-drag-hint-both');
	}

	/** Background can be affected by the parallax in home / work sections */
	public setHiddenState(hidden: boolean, force = false): void {
		if (force || this._hidden !== hidden) {
			if (!hidden) {
				this._dom.removeClass(this._$dragCursor, '-hidden');
				if (force) {
					this._dom.removeClass(this._container, '-hidecursor');
				}
			} else {
				this._dom.addClass(this._$dragCursor, '-hidden');
			}
			this._hidden = hidden;
		}
	}

	public setClickableState(clickable: boolean): void {
		this._dom.toggleClass(this._$dragCursor, '-locked', clickable);

		this._dom.toggleClass(this._container, '-clickable', clickable);
		if (this._lockClass) {
			this._dom.removeClass(this._$dragCursor, this._lockClass);
			this._lockClass = null;
		}
	}

	public transition(delay: number = 200) {
		DelayHelper.setTimeout(() => {
			this._dom.toggleClass(this._container, '-transition', true);
		}, delay);

		DelayHelper.setTimeout(() => {
			this._dom.toggleClass(this._container, '-transition', false);
		}, delay + 1400);
	}

	/**
	 * Set the special drag cursor to be active on sections that can be dragged
	 * @param {boolean} active - Turn on / off.
	 * @param {boolean} [resetPauseHoldingFlag=true] - Reset any flags that hav have been set by pause action.
	 */
	public setDragCursorActive(active: boolean, resetPauseHoldingFlag: boolean = true): void {
		if (!this._container) {
			return;
		}

		if (resetPauseHoldingFlag) {
			this._pauseHoldingFlag = false;
		}

		this._dragViewActive = active;
		this._dom.toggleClass(this._container, '-dragcursor', this._dragViewActive);

		if (active) {
			this._dom.removeClass(this._container, '-hidecursor');
			this._dom.removeClass(this._container, '-dragcursorhidden');
		}
	}

	public resetToHorizontal(): void {
		this._dom.removeClass(this._$dragCursor, '-vertical');
		this._dom.removeClass(this._$dragCursor, '-both');
	}

	public startVerticalScroll(): void {
		this._dom.addClass(this._$dragCursor, '-vertical');
		this._dom.removeClass(this._$dragCursor, '-both');
		this.setDragCursorActive(true);
	}

	public stopVerticalScroll(): void {
		this._dom.removeClass(this._$dragCursor, '-vertical');
	}

	public setBothActive(active: boolean): void {
		this._dom.addClass(this._$dragCursor, '-both');
	}

	/**
	/* Set the special drag cursor to be active on sections that can be dragged (I.E grey on sections because of the white/black mix)
	/* @param {string} colour - Color class to change the curose to
	*/
	public setColour(colour: string): void {
		if (colour === 'light') {
			this._dom.removeClass(this._container, '-darkcursor');
			return;
		}

		this._dom.addClass(this._container, '-darkcursor');
	}

	/** The class side of the drag-down state (the flags live in MouseTracking) */
	public applyDragDown(isDown: boolean, side: string = null): void {
		this._dom.toggleClass(this._$dragCursor, '-pressed', isDown);

		if (isDown) {
			let showRight,
				showLeft: boolean = true;
			if (side === 'left') {
				showRight = false;
			} else if (side === 'right') {
				showLeft = false;
			}
			this._dom.toggleClass(this._$dragCursor, '-left', showLeft);
			this._dom.toggleClass(this._$dragCursor, '-right', showRight);
		}
	}

	//Home intro flow
	//-------------------------------------------------------------------------------------

	public homeIntroStart(): void {
		this._introConstrain.setConstraints(
			{ x: device.innerWidth / 2, y: device.innerHeight * 0.5 },
			74
		);

		this._introActionActive = true;
		this.setDragCursorActive(false);
		DelayHelper.setTimeout(() => this._dom.addClass(this._$dragCursor, '-intro'), 200);

		DelayHelper.setTimeout(() => this._dom.addClass(this._$dragCursorCloneDot, '-introactive'), 1200);
	}

	public homeIntroHover(): void {
		if (!this._introActionActive) {
			return;
		}

		this._dom.addClass(this._$dragCursor, '-introhover');
		this._dom.addClass(this._$dragCursorCloneDot, '-introhover');
		this._dom.toggleClass(this._container, '-dragcursor', true);
		this._dom.addClass(document.querySelector('.home__action__guide'), '-hover');
	}

	public homeIntroMouseOut(): void {
		if (!this._introActionActive) {
			return;
		}

		this._dom.removeClass(this._$dragCursor, '-introhover');
		this._dom.removeClass(this._$dragCursorCloneDot, '-introhover');
		this._dom.toggleClass(this._container, '-dragcursor', false);
		this._dom.removeClass(document.querySelector('.home__action__guide'), '-hover');
	}

	public introComplete(): void {
		this.setDragCursorActive(true);
		this._dom.removeClass(this._$dragCursor, '-introhover');
		this._dom.removeClass(this._$dragCursor, '-intro');
		this._introActionActive = false;
	}

	//Hide-cursor hover flow (.ui_hidecursor)
	//-------------------------------------------------------------------------------------

	public mouseOver(e: MouseEvent): void {
		if (this._deps.isTouchActive()) {
			return;
		}

		if (this._dragViewActive) {
			this._dom.removeClass(this._container, '-dragcursor');
			this._dom.addClass(this._container, '-hidecursor');
			this._dom.addClass(this.$body, '-dragcursorhidden');
		}
	}

	public mouseOut(e: MouseEvent): void {
		if (this._deps.isTouchActive()) {
			return;
		}

		if (this._dragViewActive) {
			this._dom.addClass(this._container, '-dragcursor');
			this._dom.removeClass(this._container, '-hidecursor');
			this._dom.removeClass(this.$body, '-dragcursorhidden');
		}
	}

	//Sync-cursor hover-lock flow (.ui_synccursor)
	//-------------------------------------------------------------------------------------

	public syncMouseOver(e: MouseEvent): void {
		if (this._deps.isTouchActive() || globals.lastCordsFromTouch) {
			return;
		}


		this._deps.onHomeExploreMouseOver();

		return;

		//NOTE: dead (unreachable) hover-lock tail moved intact from
		//MouseController._syncMouseOver — everything below the return above has
		//never run; it is the only place $circle / $text / $dot / $text2 /
		//$circleClone / cords are ever assigned.
		if (this._clearLockhoverId) {
			DelayHelper.clearTimeout(this._clearLockhoverId);
		}

		this._hoverLocked = true;
		this._hoverLockedProcessingActive = true;

		let target = e.target as HTMLElement;

		if (!this.$circle) {
			this.$circle = (target as HTMLElement).querySelector('.ui_synccursor__circle');
		}

		if (!this.$circle) {
			target = (e.target as HTMLElement).parentElement;
			this.$circle = (target as HTMLElement).querySelector('.ui_synccursor__circle');
		}

		let lockType = target.dataset.locktype || null;
		this._lockPosition = target.dataset.lockposition || 'absoulte';

		({
			x: this._deps.getMouseCords().x,
			y: this._deps.getMouseCords().y
		});

		this.$circleClone = (target as HTMLElement).querySelector('.ui_synccursor__clone');

		this.$text = (target as HTMLElement).querySelector('.ui_synccursor__text');
		this.$dot = (target as HTMLElement).querySelector('.ui_synccursor__dot');

		if ((e.target as HTMLElement).dataset.copy2target) {
			this.$text2 = document.querySelector((e.target as HTMLElement).dataset.copy2target);
			this._dom.addClass(this.$text2, '-hovered');
		} else {
			this.$text2 = (target as HTMLElement).querySelector('.ui_synccursor__text2');
		}

		let rect = null;

		fastdom.measure(() => {
			rect = this.$circleClone.getBoundingClientRect();

			if (this._lockPosition == 'relative') {
				this._lockOrgCords = {
					x: rect.x + rect.width / 2,
					y: rect.y + rect.height / 2
				};
			} else {
				this._lockOrgCords = {
					x: rect.x,
					y: rect.y
				};
			}

			if (this._lockPosition == 'relative') {
				this._lockInertiaX.value = this._lockInertiaX.target = -90;
				this._lockInertiaY.target = this._lockInertiaY.target = -90;
			} else {
				this._lockInertiaX.value = this._lockInertiaX.target = 0;
				this._lockInertiaY.value = this._lockInertiaY.target = 0;
			}


			//Bare calls kept for side-effect parity (results were never used):
			this._deps.getMouseCords();
			this._deps.getMouseCords();

			this._lockSupportInertiaX.ease = 14;
			this._lockSupportInertiaY.ease = 14;

			this.cords = {
				x: 0,
				y: 0
			};

			this._dom.addClass(this._$dragCursor, '-locked');
			this._dom.addClass(this._container, '-locked');

			if (lockType) {
				this._lockClass = '-lockedtype-' + lockType;
				this._dom.addClass(this._$dragCursor, this._lockClass);
			}
		});
	}

	public syncMouseOut(e: MouseEvent = null): void {
		this._hoverLocked = false;

		this._dom.removeClass(this._$dragCursor, '-locked');

		if (!globals.hidingWork) {
			this._deps.onHomeExploreMouseOut();
		}

		this._dom.removeClass(this.$text2, '-hovered');

		if (this._lockClass) {
			this._dom.removeClass(this._$dragCursor, this._lockClass);
			this._lockClass = null;
		}


		fastdom.measure(() => {
			if (this.$circle) {
				//Bare call kept for side-effect parity (result was never used):
				this.$circle.getBoundingClientRect();
			}
		});

		fastdom.mutate(() => {
			if (this.$circle) {
				this.$circle.style.transform = `translate3d(${this._lockInertiaX.target}px, ${this._lockInertiaY.target}px, 0)`;
			}

			if (this.$text) {
				this.$text.style.transform = `translate3d(0px, 0px, 0)`;
			}
		});

		if (this._lockPosition == 'relative') {
			this._lockInertiaX.target = -90;
			this._lockInertiaY.target = -90;
		} else {
			this._lockInertiaX.target = 0;
			this._lockInertiaY.target = 0;
		}

		this._lockSupportInertiaX.ease = 10;
		this._lockSupportInertiaY.ease = 10;
		this._lockSupportInertiaX.target = 0;
		this._lockSupportInertiaY.target = 0;

		if (this._clearLockhoverId) {
			DelayHelper.clearTimeout(this._clearLockhoverId);
		}

		this._clearLockhoverId = DelayHelper.setTimeout(() => {
			this._hoverLockedProcessingActive = false;
		}, 500);
	}
}
